import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { useUserStore } from '@citiguide/frontend-core';
import L from 'leaflet';

interface RouteCalculatorProps {
  markerLat: number;
  markerLng: number;
  routeType: 'walking' | 'driving';
  onRouteCalculated?: (distance: number, duration: number) => void;
  onRouteError?: (error: string) => void;
}

export default function RouteCalculator({
  markerLat,
  markerLng,
  routeType,
  onRouteCalculated,
  onRouteError,
}: RouteCalculatorProps) {
  const map = useMap();
  const routeLayerRef = useRef<L.Polyline | null>(null);
  const { location } = useUserStore();

  useEffect(() => {
    if (!map || !location) return;

    // Remove existing route if any
    if (routeLayerRef.current) {
      map.removeLayer(routeLayerRef.current);
      routeLayerRef.current = null;
    }

    const startPoint: [number, number] = [location[0], location[1]];
    const endPoint: [number, number] = [markerLat, markerLng];

    // Use OSRM API for route calculation
    const profile = routeType === 'walking' ? 'foot' : 'car';
    const url = `https://router.project-osrm.org/route/v1/${profile}/${startPoint[1]},${startPoint[0]};${endPoint[1]},${endPoint[0]}?overview=full&geometries=geojson`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          const route = data.routes[0];
          const geometry = route.geometry;

          // Create polyline from route geometry
          const coordinates = geometry.coordinates.map((coord: [number, number]) => [coord[1], coord[0]] as [number, number]);
          const polyline = L.polyline(coordinates, {
            color: routeType === 'walking' ? '#00aaff' : '#ff6600',
            weight: 4,
            opacity: 0.7,
          });

          polyline.addTo(map);
          routeLayerRef.current = polyline;

          // Fit map to route bounds
          map.fitBounds(polyline.getBounds(), { padding: [50, 50] });

          // Calculate distance and duration
          const distance = route.distance / 1000; // Convert to km
          const duration = route.duration / 60; // Convert to minutes

          onRouteCalculated?.(distance, duration);
        } else {
          onRouteError?.('Route not found');
        }
      })
      .catch((error) => {
        onRouteError?.(error.message || 'Failed to calculate route');
      });

    return () => {
      if (routeLayerRef.current) {
        map.removeLayer(routeLayerRef.current);
        routeLayerRef.current = null;
      }
    };
  }, [map, location, markerLat, markerLng, routeType, onRouteCalculated, onRouteError]);

  return null;
}

